/**
 * EditorContentElement — the editor's interactive scene element, built on
 * web-core's `CreatorElement` base.
 *
 * This is the convergence point of the editor onto web-core: the element's
 * transform handle, identity and lifecycle come from `CreatorElement` (shared
 * with the boilerplates), while the EDITOR-specific concerns live here —
 * selection/hover highlight, control-mode dimming, grow tweens, picker metadata
 * and loading via the editor's `AssetManager`.
 *
 * Coordinate space: the editor works entirely in its internal (right-handed,
 * Y-up) space — positions/rotations are applied to the anchor DIRECTLY (no Unity
 * z-flip / panel flip). So the base's Unity-space convert helpers are
 * intentionally bypassed (applyTransform/readTransform are overridden), keeping
 * the on-screen result byte-identical to the previous editor.
 */

import {
  StandardMaterial,
  Vector3,
  Quaternion,
  type Mesh,
  type TransformNode,
} from 'babylonjs';
import CreatorElement from '@webcore/core/creator/creator.element';
import { CreatorFileImageType, type CreatorFile, type CreatorSceneChild } from '@webcore/core/creator/types';
import {
  quaternionFromEulerDegreesXYZ,
  eulerDegreesXYZFromQuaternion,
  clampPositionArray,
} from '../utils/BabylonEditorUtils';
import { ControlModeTypes } from '@shared/constants';
import type { ElementStateData } from './EditorContentTypes';
import type { EditorState } from '@shared/EditorState';
import type { EventBus } from '@shared/events/EventBus';
import type { EditorEvents } from '@shared/events/EventTypes';
import type { AssetManager } from '../assets/AssetManager';

/**
 * The structural contract the Picker / TransformController / Editor depend on.
 * Both element kinds (panel + model) satisfy it, so those consumers stay
 * element-agnostic. The gizmo/focus/pick handle is the `anchor` (a TransformNode);
 * Babylon's GizmoManager attaches to it via attachToNode.
 */
export interface EditorElementLike {
  readonly anchor: TransformNode;
  readonly sceneID: string;
  isSelected: boolean;
  setTransform(
    transform: { position: number[]; rotation: number[]; scale: number[] },
    animate?: boolean,
  ): void;
  handleHoverIn(): void;
  handleHoverOut(): void;
  update(): void;
  dispose(): void;
}

function toTuple(a: number[]): [number, number, number] {
  return [a[0], a[1], a[2]];
}

export abstract class EditorContentElement extends CreatorElement implements EditorElementLike {
  isSelected = false;
  isHovered = false;

  protected state: EditorState;
  protected events: EventBus<EditorEvents>;
  protected assetManager: AssetManager;
  protected overlayMat: StandardMaterial | null = null;
  protected overlay: Mesh | null = null;
  protected targetScale: Vector3;
  // Set true on dispose; guards late async asset loads from parenting an orphan
  // onto a disposed anchor (loadSceneConfig can run twice on load).
  protected _disposed = false;
  private eventCleanups: Array<() => void> = [];

  constructor(
    parent: TransformNode,
    data: ElementStateData,
    scene: import('babylonjs').Scene,
    state: EditorState,
    events: EventBus<EditorEvents>,
    assetManager: AssetManager,
  ) {
    // Feed the base an INTERNAL-space child (the editor's space). The base stores
    // identity (sceneID/assetId) + the anchor; the file is unused by the editor
    // (assets load via AssetManager by assetID), so a stub satisfies the type.
    const child: CreatorSceneChild = {
      sceneID: data.sceneID,
      assetID: data.assetID ?? '',
      position: toTuple(data.position),
      rotation: toTuple(data.rotation),
      scale: toTuple(data.scale),
    };
    const fileStub: CreatorFile = { type: CreatorFileImageType, path: '' };
    super(scene, parent, child, fileStub);

    this.state = state;
    this.events = events;
    this.assetManager = assetManager;
    this.targetScale = this.scaleVector(data.scale);

    // The anchor is the gizmo/pick/transform handle.
    this.anchor.metadata = { editorElement: this, sceneID: this.sceneID };

    // Initial transform (internal space); start tiny and grow on asset load.
    this.applyTransformImmediate(data);
    this.anchor.scaling.setAll(0.01);

    this.initStateManagement();
  }

  // ----- subclass hooks ------------------------------------------------------

  /** Map a data scale [x,y,z] to the anchor scale vector (panels force z=1). */
  protected abstract scaleVector(scale: number[]): Vector3;

  /** Set the element's visual opacity (panels dim their material; models no-op). */
  abstract setOpacity(opacity: number): void;

  /** Dispose the visual (material / instance) — NOT the anchor (base handles it). */
  protected abstract disposeVisual(): void;

  // ----- transform (internal space, applied directly to the anchor) ----------

  protected applyTransformImmediate(data: ElementStateData): void {
    this.anchor.position.set(data.position[0], data.position[1], data.position[2]);
    this.anchor.rotationQuaternion = quaternionFromEulerDegreesXYZ(
      data.rotation[0], data.rotation[1], data.rotation[2],
    );
  }

  setTransform(
    { position, rotation, scale }: { position: number[]; rotation: number[]; scale: number[] },
    animate = false,
  ): void {
    this.anchor.rotationQuaternion = quaternionFromEulerDegreesXYZ(rotation[0], rotation[1], rotation[2]);
    const target = this.scaleVector(scale);
    this.targetScale = target;
    const [px, py, pz] = clampPositionArray(position);
    if (animate) {
      this.tweenScaleTo(target);
      this.tweenPositionTo(new Vector3(px, py, pz));
    } else {
      this.anchor.position.set(px, py, pz);
      this.anchor.scaling.copyFrom(target);
    }
  }

  /** Read the anchor's transform back in internal (right-handed, Y-up) space. */
  override readTransform(): {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
  } {
    const p = this.anchor.position;
    const q = this.anchor.rotationQuaternion ?? Quaternion.FromEulerVector(this.anchor.rotation);
    const s = this.anchor.scaling;
    return {
      position: [p.x, p.y, p.z],
      rotation: eulerDegreesXYZFromQuaternion(q),
      scale: [s.x, s.y, s.z],
    };
  }

  protected tweenScaleTo(target: Vector3): void {
    const obs = this.scene.onBeforeRenderObservable.add(() => {
      const s = this.anchor.scaling;
      s.x += (target.x - s.x) * 0.2;
      s.y += (target.y - s.y) * 0.2;
      s.z += (target.z - s.z) * 0.2;
      if (Math.abs(target.x - s.x) < 0.001 && Math.abs(target.y - s.y) < 0.001 && Math.abs(target.z - s.z) < 0.001) {
        s.copyFrom(target);
        this.scene.onBeforeRenderObservable.remove(obs);
      }
    });
  }

  protected tweenPositionTo(target: Vector3): void {
    const obs = this.scene.onBeforeRenderObservable.add(() => {
      const p = this.anchor.position;
      p.x += (target.x - p.x) * 0.25;
      p.y += (target.y - p.y) * 0.25;
      p.z += (target.z - p.z) * 0.25;
      if (Vector3.Distance(p, target) < 0.001) {
        p.copyFrom(target);
        this.scene.onBeforeRenderObservable.remove(obs);
      }
    });
  }

  // ----- selection / hover / control-mode (shared) ---------------------------

  private initStateManagement(): void {
    const onSelected = (payload: { elementId: string }) => {
      if (payload.elementId === this.sceneID) {
        this.isSelected = true;
        this.isHovered = true;
        this.setHighlight(0.4);
      } else if (this.isSelected) {
        this.handleDeselected();
      }
    };
    this.events.on('element:selected', onSelected);
    this.eventCleanups.push(() => this.events.off('element:selected', onSelected));

    const onDeselected = () => { if (this.isSelected) this.handleDeselected(); };
    this.events.on('element:deselected', onDeselected);
    this.eventCleanups.push(() => this.events.off('element:deselected', onDeselected));

    const onControlMode = (payload: { mode: number }) => {
      if (payload.mode === ControlModeTypes.TRANSFORM) {
        this.setOpacity(this.isSelected ? 1 : 0.5);
      } else if (payload.mode === ControlModeTypes.NAVIGATE_SCENE) {
        this.setOpacity(0.5);
      } else {
        this.setOpacity(1);
      }
    };
    this.events.on('controlMode:changed', onControlMode);
    this.eventCleanups.push(() => this.events.off('controlMode:changed', onControlMode));
  }

  private handleDeselected(): void {
    this.isSelected = false;
    this.setHighlight(0);
  }

  protected setHighlight(opacity: number): void {
    if (this.overlayMat) this.overlayMat.alpha = opacity;
  }

  handleHoverIn(): void {
    this.isHovered = true;
    this.setHighlight(this.isSelected ? 0.6 : 0.4);
  }

  handleHoverOut(): void {
    this.isHovered = false;
    this.setHighlight(this.isSelected ? 0.4 : 0);
  }

  // ----- metadata / lifecycle ------------------------------------------------

  /** Stamp { editorElement, sceneID } on the anchor + every visual mesh so a pick
   *  anywhere on the element resolves it (the editor's Picker reads editorElement). */
  override stampMetadata(): void {
    const metadata = { editorElement: this, sceneID: this.sceneID };
    this.anchor.metadata = metadata;
    for (const mesh of this.anchor.getChildMeshes(false)) mesh.metadata = metadata;
  }

  update(): void {
    // No per-frame work; the gizmo follows the anchor, animations self-advance.
  }

  override dispose(): void {
    this._disposed = true;
    this.eventCleanups.forEach((fn) => fn());
    this.eventCleanups = [];
    this.overlayMat?.dispose();
    this.overlay?.dispose();
    this.disposeVisual();
    // Dispose the anchor + its visual subtree, but NOT materials/textures (models
    // share them with the cached template; panels dispose their own in disposeVisual).
    this.anchor.dispose();
  }
}

export default EditorContentElement;
